import { __decorate } from 'tslib';
import * as i0 from '@angular/core';
import { Directive, Input, HostListener, Component, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { Debounce } from '@js-dot/core';
import * as i1 from '@ng-dot/core';
const {
  keys: KEYS
} = Object;
const {
  isArray: IS_ARRAY
} = Array;
/**
 * Set the Class Name by the actual size obtained from the element's parent element.
 *
 * @example
 * simple widths:
 * ```angular
 * <div [dotContainer]="[960, 1440]"></div>
 * ```
 * > Expected Class Names:
 * > * _width-960
 * > * _width-1440
 *
 * Customized Class Names:
 * ```js
 * <div [dotContainer]="{'960': 'desktop', '1440': ['wide', 'over1440']}"></div>
 * ```
 * > Expected Class Names:
 * > * _width-960 desktop
 * > * _width-1440 wide over1440
 */
class DotContainerDirective {
  _renderer;
  prefix = '_width-';
  set fromSelector(value) {
    if (value) {
      this.flags = value;
    }
  }
  set flags(value) {
    const {
      prefix
    } = this;
    const fixedFlags = [];
    let current = -1;
    if (IS_ARRAY(value)) {
      const end = value.length;
      while (++current < end) {
        const flag = value[current];
        if (typeof flag === 'number') {
          fixedFlags[fixedFlags.length] = [flag, [prefix + flag]];
        } else {
          const size = flag[0];
          if (IS_ARRAY(size)) {
            throw new ReferenceError('DotContainerFlag Item first value should be a number type');
          }
          const classNames = flag[1] || [];
          const prefixedSize = prefix + size;
          fixedFlags[fixedFlags.length] = [size, IS_ARRAY(classNames) ? classNames.concat([prefixedSize]) : [prefixedSize, classNames]];
        }
      }
    } else {
      const keys = KEYS(value);
      const end = keys.length;
      while (++current < end) {
        const size = +keys[current];
        const prefixedSize = prefix + size;
        let classNames = value[size];
        fixedFlags[fixedFlags.length] = classNames ? [size, IS_ARRAY(classNames) ? classNames.concat([prefixedSize]) : [classNames, prefixedSize]] : [size, [prefixedSize]];
      }
    }
    this._flags = fixedFlags;
    if (this._size) {
      this._setSize();
    }
  }
  get flags() {
    return this._flags;
  }
  _flags;
  _size;
  _lastClassNames;
  _nextWidth;
  _lastWidth;
  host;
  hidden = true;
  constructor(_renderer, viewContainerRef, elementRef) {
    this._renderer = _renderer;
    viewContainerRef.createComponent(DotContainerStyleComponent).destroy();
    this.host = elementRef.nativeElement;
    if (this.host.clientWidth === 0) {
      if (!['table', 'block', 'inline-block', 'flex', 'inline-flex', 'grid'].includes(getComputedStyle(this.host)['display'])) {
        _renderer.setStyle(this.host, 'display', 'block');
      }
    }
  }
  ngOnInit() {
    const {
      _renderer,
      host
    } = this;
    const area = _renderer.tag('.dot-container-area');
    const size = _renderer.tag('.dot-container-size');
    this._size = _renderer.append(area, size);
    _renderer.before(host, area);
    if (!this._flags) {
      this.flags = [420, 480, 540, 640, 760, 1000, 1200, 1400, 1600, 1800, 2000, 2200, 2400];
    } else {
      this._setSize();
    }
  }
  ngOnDestroy() {
    this._renderer.remove(this._size);
  }
  _onResize() {
    this._setSize();
  }
  _setSize() {
    const {
      _size,
      _lastWidth,
      _nextWidth
    } = this;
    const width = _size.scrollWidth;
    if (_nextWidth && _lastWidth <= width && _nextWidth > width) {
      return;
    }
    const {
      _flags,
      _lastClassNames,
      _renderer,
      host
    } = this;
    this._nextWidth = undefined;
    const stacks = [];
    const end = _flags.length;
    let current = -1;
    let lastFlag;
    while (++current < end) {
      const [flagWidth, classNames] = _flags[current];
      if (flagWidth > width) {
        this._nextWidth = flagWidth;
        break;
      }
      stacks.push(...classNames);
      lastFlag = flagWidth;
    }
    this._lastWidth = lastFlag;
    if (_lastClassNames) {
      _renderer.removeClass(host, _lastClassNames);
    }
    _renderer.addClass(host, stacks);
    this._lastClassNames = stacks;
  }
  static ɵfac = function DotContainerDirective_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotContainerDirective)(i0.ɵɵdirectiveInject(i1.DotRendererService), i0.ɵɵdirectiveInject(i0.ViewContainerRef), i0.ɵɵdirectiveInject(i0.ElementRef));
  };
  static ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
    type: DotContainerDirective,
    selectors: [["", "dotContainer", ""]],
    hostBindings: function DotContainerDirective_HostBindings(rf, ctx) {
      if (rf & 1) {
        i0.ɵɵlistener("resize", function DotContainerDirective_resize_HostBindingHandler() {
          return ctx._onResize();
        }, false, i0.ɵɵresolveWindow);
      }
    },
    inputs: {
      prefix: "prefix",
      fromSelector: [0, "dotContainer", "fromSelector"],
      flags: "flags"
    }
  });
}
__decorate([Debounce(96, true)], DotContainerDirective.prototype, "_setSize", null);
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotContainerDirective, [{
    type: Directive,
    args: [{
      standalone: true,
      selector: '[dotContainer]'
      // host: {
      // 	'[style.visibility]': 'hidden ? "hidden" : "inherit"'
      // }
    }]
  }], () => [{
    type: i1.DotRendererService
  }, {
    type: i0.ViewContainerRef
  }, {
    type: i0.ElementRef
  }], {
    prefix: [{
      type: Input
    }],
    fromSelector: [{
      type: Input,
      args: ['dotContainer']
    }],
    flags: [{
      type: Input
    }],
    _onResize: [{
      type: HostListener,
      args: ['window:resize']
    }],
    _setSize: []
  });
})();
class DotContainerStyleComponent {
  static ɵfac = function DotContainerStyleComponent_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotContainerStyleComponent)();
  };
  static ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
    type: DotContainerStyleComponent,
    selectors: [["ng-component"]],
    decls: 0,
    vars: 0,
    template: function DotContainerStyleComponent_Template(rf, ctx) {},
    styles: [".dot-container-area{position:relative}.dot-container-size{position:absolute;left:0;right:0}\n"],
    encapsulation: 2,
    changeDetection: 0
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotContainerStyleComponent, [{
    type: Component,
    args: [{
      standalone: true,
      template: ``,
      encapsulation: ViewEncapsulation.None,
      changeDetection: ChangeDetectionStrategy.OnPush,
      styles: [".dot-container-area{position:relative}.dot-container-size{position:absolute;left:0;right:0}\n"]
    }]
  }], null, null);
})();

/**
 * Generated bundle index. Do not edit.
 */

export { DotContainerDirective, DotContainerStyleComponent };
